import { Button, Input, Select } from '@/components'
import { useEffect, useRef, useState } from 'react'
import { Editor } from '@toast-ui/react-editor'
import codeSyntaxHighlight from '@toast-ui/editor-plugin-code-syntax-highlight'
import colorSyntax from '@toast-ui/editor-plugin-color-syntax'
import '@toast-ui/editor/dist/i18n/ko-kr'
import '@toast-ui/editor/dist/toastui-editor.css'
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css'
import 'tui-color-picker/dist/tui-color-picker.css'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/redux/hooks'
import {
  buttonName,
  confirmMessages,
  errorMessages,
  fieldMessages,
} from '@/pages/board/boardItem'
import { FileUploadResponse } from '../blogItem'
import { useResponsive } from '@/hooks/useResponsive'

export type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export const options = [
  { value: '', label: '선택' },
  { value: '01', label: '잡담' },
  { value: '02', label: '질문' },
  { value: '00', label: '기타' },
]

export type FormData = {
  bbsSj: string
  nttSj: string
  nttCn: string
}

export const BlogEditor = () => {
  const axios = useAxios()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()
  const editorRef = useRef<Editor>(null)
  const [imgUrl, setImgUrl] = useState<string>('')
  const [fileSeqList, setFileSeqList] = useState<number[]>([])
  const [fileNameList, setFileNameList] = useState<string[]>([])
  const { isMobile, isTablet, isDesktop } = useResponsive()
  // 이미지 정보를 저장할 ref (URL -> {width, height})
  const imageInfoMapRef = useRef<
    Map<string, { width: number; height: number }>
  >(new Map())

  // 최종 저장용 HTML state (사이즈 정보 포함)
  const [finalNttCn, setFinalNttCn] = useState<string>('')

  const [formData, setFormData] = useState({
    cmpnyCd: '001',
    cstId: user?.cstId || '',
    nttSn: 0,
    regId: user?.cstId || '',
    regDate: '',
    updId: user?.cstId || '',
    updDate: '',
    bbsTy: '01',
    bbsSj: '',
    nttSj: '',
    nttCn: '',
    imgUrl: imgUrl,
    page: 1,
    srchOpt: '',
    srchStr: '',
    option: '',
    accessIp: '',
    fileSeqList: fileSeqList,
    fileNameList: fileNameList,
  })

  // 모든 이미지에 스타일을 적용하는 함수
  const applyImageStyles = (html: string) => {
    let styledHTML = html

    // 텍스트 + 이미지가 한 문단에 같이 있는 경우 분리
    styledHTML = styledHTML.replace(
      /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
      (_, textBefore, imgTag, textAfter) => {
        const parts = []
        if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
        parts.push(`<p>${imgTag}</p>`)
        if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
        return parts.join('')
      },
    )

    // 마지막이 <img><br> 로 끝날 경우 개행용 <p> 추가
    if (styledHTML.match(/<p><img[^>]+><br><\/p>$/)) {
      styledHTML += '<p></p>'
    }

    // 모든 이미지에 스타일 적용
    // imageInfoMapRef.current.forEach(({ width, height }, url) => {
    //   const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    //   // 이미 style이 있는 경우와 없는 경우 모두 처리
    //   const imgRegex = new RegExp(
    //     `<img([^>]*?)src="${escapedUrl}"(?:[^>]*?style="[^"]*")?([^>]*?)>`,
    //     'g',
    //   )

    //   styledHTML = styledHTML.replace(
    //     imgRegex,
    //     `<img src="${url}" style="width: ${width}px; height: ${height}px;" />`,
    //   )
    // })

    return styledHTML
  }

  const uploadImageToServer = async (
    blob: Blob,
  ): Promise<{ fileUrl: string; fileSeq: number | null }> => {
    const formData = new FormData()
    formData.append('file', blob)

    try {
      const response = await axios.post<{ data: FileUploadResponse }>(
        '/blog/fileUpload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )

      const fileUrl =
        process.env.REACT_APP_BASE_URL + '/upload/' + response.data.data.fileNm
      const fileSeq = response.data.data.fileSeq
      const fileName = response.data.data.fileNm

      setImgUrl(fileUrl)
      setFileSeqList((prevList) => [...prevList, fileSeq])
      setFileNameList((prevList) => [...prevList, fileName])

      return { fileUrl, fileSeq }
    } catch (error) {
      console.error('이미지 업로드 실패:', error)
      return { fileUrl: '', fileSeq: null }
    }
  }

  // useEffect(() => {
  //   if (editorRef.current) {
  //     editorRef.current
  //       .getInstance()
  //       .addHook(
  //         'addImageBlobHook',
  //         async (
  //           blob: Blob,
  //           callback: (url: string, altText: string) => void,
  //         ) => {
  //           const maxFileSize = 10 * 1024 * 1024 // 10MB

  //           if (blob.size > maxFileSize) {
  //             showAlert({
  //               title: '10MB 이상의 파일은 업로드 할 수 없어요.',
  //               message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
  //             })
  //             return
  //           }

  //           // 원본 이미지 크기 확인
  //           const checkImageSize = new Promise<{
  //             width: number
  //             height: number
  //           }>((resolve) => {
  //             const img = new Image()
  //             img.onload = () => {
  //               resolve({ width: img.naturalWidth, height: img.naturalHeight })
  //             }
  //             img.src = URL.createObjectURL(blob)
  //           })

  //           const originalSize = await checkImageSize
  //           console.log('원본 이미지 크기:', originalSize)

  //           const { fileSeq, fileUrl } = await uploadImageToServer(blob)

  //           if (fileUrl) {
  //             console.log('[DEBUG] 이미지 URL 획득:', fileUrl)

  //             // 이미지 정보를 ref에 저장
  //             imageInfoMapRef.current.set(fileUrl, {
  //               width: originalSize.width,
  //               height: originalSize.height,
  //             })

  //             // 1. 에디터 화면에는 일반 이미지로 표시 (사용자가 볼 수 있도록)
  //             callback(fileUrl, '')

  //             // 2. 짧은 지연 후 finalNttCn state 업데이트 (모든 이미지에 사이즈 정보 포함)
  //             setTimeout(() => {
  //               const editorInstance = editorRef.current?.getInstance()
  //               if (!editorInstance) return

  //               const currentHTML = editorInstance.getHTML()
  //               console.log('[DEBUG] 현재 에디터 HTML:', currentHTML)

  //               // 모든 이미지에 스타일을 적용한 HTML 생성
  //               const styledHTML = applyImageStyles(currentHTML)

  //               console.log('[DEBUG] 최종 HTML 업데이트:', styledHTML)
  //               setFinalNttCn(styledHTML)
  //             }, 100)
  //           }
  //           return false
  //         },
  //       )
  //   }
  // }, [])

  useEffect(() => {
    console.log('========== 디버깅 시작 ==========')
    console.log('isMobile:', isMobile)
    console.log('isTablet:', isTablet)
    console.log('isDesktop:', isDesktop)
    console.log('window.innerWidth:', window.innerWidth)
    console.log('editorRef.current:', editorRef.current)
    console.log('================================')
    // 약간의 지연을 두고 실행 (에디터가 완전히 마운트될 때까지)
    const timer = setTimeout(() => {
      console.log('🔍 useEffect 실행 - editorRef:', editorRef.current)
      console.log('🔍 isMobile:', isMobile)

      if (editorRef.current) {
        console.log('✅ Editor 인스턴스 획득')

        editorRef.current
          .getInstance()
          .addHook(
            'addImageBlobHook',
            async (
              blob: Blob,
              callback: (url: string, altText: string) => void,
            ) => {
              console.log('🔥 addImageBlobHook 실행됨!', blob)
              console.log('🔥 isMobile:', isMobile)

              const maxFileSize = 10 * 1024 * 1024 // 10MB

              if (blob.size > maxFileSize) {
                showAlert({
                  title: '10MB 이상의 파일은 업로드 할 수 없어요.',
                  message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
                })
                return
              }

              const { fileSeq, fileUrl } = await uploadImageToServer(blob)

              if (fileUrl) {
                callback(fileUrl, '')

                const editorInstance = editorRef.current?.getInstance()
                const currentHTML = editorInstance?.getHTML()

                if (!currentHTML) return

                let fixedHTML = currentHTML.replace(
                  /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
                  (_, textBefore, imgTag, textAfter) => {
                    const parts = []
                    if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
                    parts.push(`<p>${imgTag}</p>`)
                    if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
                    return parts.join('')
                  },
                )

                // style 속성 제거
                fixedHTML = fixedHTML.replace(
                  /<img([^>]*)\s+style="[^"]*"([^>]*)>/g,
                  '<img$1$2>',
                )

                if (fixedHTML.match(/<p><img[^>]+><br><\/p>$/)) {
                  fixedHTML += '<p></p>'
                }

                editorInstance?.setHTML(fixedHTML)
              }
              return false
            },
          )
      } else {
        console.log('❌ editorRef.current가 null')
      }
    }, 200) // 200ms 지연

    return () => clearTimeout(timer)
  }, [isMobile]) // isMobile 의존성 추가

  // 에디터 내용이 변경될 때마다 finalNttCn도 업데이트
  const handleEditorChange = () => {
    const editorInstance = editorRef.current?.getInstance()
    if (editorInstance) {
      const currentHTML = editorInstance.getHTML()

      // formData의 nttCn은 화면 표시용
      setFormData((prev) => ({ ...prev, nttCn: currentHTML }))

      // 모든 이미지에 스타일을 적용한 HTML로 finalNttCn 업데이트
      const styledHTML = applyImageStyles(currentHTML)
      setFinalNttCn(styledHTML)
    }
  }

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      fileSeqList: fileSeqList,
      fileNameList: fileNameList,
    }))
  }, [fileSeqList, fileNameList])

  const mySanitizer = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    doc.querySelectorAll('img').forEach((img) => {
      img.setAttribute('data-category', 'uploaded')
      img.setAttribute('data-id', '0')
    })

    return doc.body.innerHTML
  }

  const handleChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleModalAction = (type: 'insert' | 'cancel') => {
    if (type === 'insert') {
      const requiredFields: {
        field: keyof FormData
        message: string
      }[] = [
        { field: 'bbsSj', message: fieldMessages.bbsSj },
        { field: 'nttSj', message: fieldMessages.nttSj },
        { field: 'nttCn', message: fieldMessages.nttCn },
      ]

      // 필수 입력값 확인 (finalNttCn 기준으로)
      for (const { field, message } of requiredFields) {
        let value = formData[field]

        // nttCn 검증은 finalNttCn 사용
        if (field === 'nttCn') {
          value = finalNttCn || formData.nttCn
        }

        if (field === 'nttCn') {
          // nttCn이 HTML 형태일 경우, 이미지 태그는 허용하고 나머지 태그는 제거
          const strippedText = value
            .replace(/<(?!img\s*[^>]*>)[^>]+>/g, ' ')
            .trim() // img 태그를 제외한 나머지 HTML 태그 제거

          // 내용이 없거나 공백만 있는 경우
          if (
            strippedText === '' ||
            strippedText.replace(/\s+/g, '').length === 0
          ) {
            showAlert({
              type: 'warning',
              title: message,
            })
            return
          }
        } else if (typeof value === 'string' && !value.trim()) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }

        if (!value) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }
      }

      showConfirm({
        title: confirmMessages.insert,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          insertBlog()
        },
      })
    }

    if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(`/blog`, { state: location.state })
          }, 100)
        },
      })
    }
  }

  const insertBlog = async () => {
    try {
      // 최종 제출 시 finalNttCn을 formData에 설정
      const submitFormData = {
        ...formData,
        nttCn: finalNttCn || formData.nttCn, // finalNttCn이 있으면 사용, 없으면 기본 nttCn 사용
      }

      console.log('[DEBUG] 최종 제출할 HTML:', submitFormData.nttCn)

      await axios.post(`/blog/insert`, submitFormData)

      setTimeout(() => {
        navigate(`/blog`, { state: location.state })
      }, 300)
    } catch (err: any) {
      showAlert({
        type: 'error',
        title: errorMessages.insert,
        message: err.response?.data?.message,
        btnName: buttonName.confirm,
      })
    }
  }

  return (
    <>
      {!isMobile ? (
        <>
          <div
            style={{
              marginBottom: '10px',
              display: 'flex',
              gap: '5px',
            }}
          >
            <Select
              id="bbsSj"
              name="bbsSj"
              width="200px"
              value={formData.bbsSj}
              onChange={handleChange}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Input
              id="nttSj"
              name="nttSj"
              placeholder="제목을 입력하세요."
              maxLength={255}
              value={formData.nttSj}
              onChange={handleChange}
            />
          </div>
          <Editor
            ref={editorRef}
            useCommandShortcut={true}
            placeholder="내용을 입력해 주세요!"
            previewStyle="vertical"
            height="500px"
            initialEditType="wysiwyg"
            onChange={handleEditorChange}
            language="ko-KR"
            hideModeSwitch={true}
            plugins={[colorSyntax, codeSyntaxHighlight]}
            customHTMLSanitizer={mySanitizer}
          />
          <div
            style={{
              display: 'flex',
              gap: '5px',
              justifyContent: 'flex-end',
              marginTop: '10px',
            }}
          >
            <Button color="neverbe" onClick={() => handleModalAction('insert')}>
              저장
            </Button>
            <Button color="neverbe" onClick={() => handleModalAction('cancel')}>
              취소
            </Button>
          </div>
        </>
      ) : (
        <div className="blog-write-container">
          <div className="blog-write-header">
            <h1 className="blog-write-title">소통</h1>
            <button
              className="blog-back-btn"
              onClick={() => handleModalAction('cancel')}
            >
              ← 뒤로
            </button>
          </div>
          <div className="blog-write-card">
            <form className="blog-write-form">
              <div className="blog-form-row">
                <div className="blog-form-group category">
                  <label className="blog-form-label">카테고리</label>
                  <select
                    className="blog-form-select"
                    id="bbsSj"
                    name="bbsSj"
                    value={formData.bbsSj}
                    onChange={handleChange}
                    required
                  >
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="blog-form-group title">
                  <label className="blog-form-label">제목</label>
                  <input
                    type="text"
                    className="blog-form-input"
                    id="nttSj"
                    name="nttSj"
                    placeholder="제목을 입력하세요"
                    value={formData.nttSj}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="blog-form-group">
                <Editor
                  ref={editorRef}
                  useCommandShortcut={true}
                  placeholder="내용을 입력해 주세요!"
                  previewStyle="vertical"
                  height="500px"
                  initialEditType="wysiwyg"
                  onChange={handleEditorChange}
                  language="ko-KR"
                  hideModeSwitch={true}
                  plugins={[colorSyntax, codeSyntaxHighlight]}
                  customHTMLSanitizer={mySanitizer}
                />
              </div>
              <div className="blog-post-actions">
                <button
                  type="button"
                  className="blog-action-btn primary"
                  onClick={() => handleModalAction('insert')}
                >
                  저장
                </button>
                <>
                  <button
                    type="button"
                    className="blog-action-btn user"
                    onClick={() => handleModalAction('cancel')}
                  >
                    취소
                  </button>
                </>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
